<template>
  <!-- モーダル -->
  <client-only>
    <Teleport to="body">
      <dialog
        ref="calendarModal"
        class="modal"
      >
        <form
          class="modal-box lg:min-w-[40rem]"
          @submit.prevent
        >
          <!-- 右上の閉じるボタン -->
          <div
            class="btn btn-circle btn-ghost btn-sm absolute right-2 top-2"
            @click="closeModal"
          >
            ✕
          </div>
          <!-- モーダルのコンテンツ -->
          <div class="te-modal-body pb-[6.25rem] pt-5">
            <h4 class="co-display-12 mb-8 border-b border-[#dee2e6] pb-5 font-bold">
              相談日時の予約登録
            </h4>
            <div class="text-center">
              <p class="co-display-12 m-0 text-left md:text-center">
                日時をご確認いただき、必要事項をご記入の上、申請してください。
              </p>
              <p class="co-display-11 mb-10 mt-5 inline-block bg-co-gray-200 p-5 text-center font-bold md:px-12 md:py-4">
                {{ dateToDateStr(appointmentDate) }}〜
                <span class="co-display-13 ml-3 whitespace-nowrap rounded-full bg-co-gray-600 px-2 py-1 font-bold leading-none text-white">
                  <FontAwesomeIcon
                    :icon="faClockNine"
                    class="fa-fw mr-1"
                  />最大60分
                </span>
              </p>
            </div>

            <!-- フォーム -->
            <!-- 氏名 -->
            <div class="flex flex-col border-b border-dotted border-co-gray-300 pb-5 pt-4 md:flex-row md:gap-x-8">
              <label
                class="basis-full py-2 font-bold md:basis-4/12 md:text-right"
                for="entry_last_name"
              >
                氏名
                <YaTeCommonBadgeRequired class="ml-2" />
              </label>
              <div class="basis-full md:basis-8/12">
                <div class="flex gap-x-4">
                  <div class="basis-6/12">
                    <input
                      id="entry_last_name"
                      v-model="lastName"
                      class="input w-full border-2 border-co-gray-300"
                      placeholder="山田"
                      type="text"
                      required
                    >
                    <span
                      v-if="errors.last_name && errors.last_name.length !== 0"
                      class="te-invalid-feedback inline-block"
                    >
                      {{ errors.last_name.join(", ") }}
                    </span>
                  </div>
                  <div class="basis-6/12">
                    <input
                      v-model="firstName"
                      class="input w-full border-2 border-co-gray-300"
                      placeholder="太郎"
                      type="text"
                      required
                    >
                    <span
                      v-if="errors.first_name && errors.first_name.length !== 0"
                      class="te-invalid-feedback inline-block"
                    >
                      {{ errors.first_name.join(", ") }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- 氏名（カナ） -->
            <div class="flex flex-col border-b border-dotted border-co-gray-300 pb-5 pt-4 md:flex-row md:gap-x-8">
              <label
                class="basis-full py-2 font-bold md:basis-4/12 md:text-right"
                for="entry_last_name_kana"
              >
                氏名（カナ）
                <YaTeCommonBadgeRequired class="ml-2" />
              </label>
              <div class="basis-full md:basis-8/12">
                <div class="flex gap-x-4">
                  <div class="basis-6/12">
                    <input
                      id="entry_last_name_kana"
                      v-model="lastNameKana"
                      placeholder="ヤマダ"
                      type="text"
                      required
                      class="input w-full border-2 border-co-gray-300"
                    >
                    <span
                      v-if="errors.last_name_kana && errors.last_name_kana.length !== 0"
                      class="te-invalid-feedback inline-block"
                    >
                      {{ errors.last_name_kana.join(", ") }}
                    </span>
                  </div>
                  <div class="basis-6/12">
                    <input
                      v-model="firstNameKana"
                      placeholder="タロウ"
                      type="text"
                      required
                      class="input w-full border-2 border-co-gray-300"
                    >
                    <span
                      v-if="errors.first_name_kana && errors.first_name_kana.length !== 0"
                      class="te-invalid-feedback inline-block"
                    >
                      {{ errors.first_name_kana.join(", ") }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- メールアドレス -->
            <div class="flex flex-col border-b border-dotted border-co-gray-300 pb-5 pt-4 md:flex-row md:gap-x-8">
              <label
                class="basis-full py-2 font-bold md:basis-4/12 md:text-right"
                for="entry_email"
              >
                メールアドレス
                <YaTeCommonBadgeRequired class="ml-2" />
              </label>
              <div class="basis-full md:basis-8/12">
                <input
                  id="entry_email"
                  v-model="email"
                  placeholder="taro.yamada@lytechacademy.jp"
                  type="email"
                  required
                  class="input w-full border-2 border-co-gray-300"
                >
                <span
                  v-if="errors.email && errors.email.length !== 0"
                  class="te-invalid-feedback inline-block"
                >
                  {{ errors.email.join(", ") }}
                </span>
              </div>
            </div>

            <!-- 興味のあるコース -->
            <div class="w-full pb-8 pt-5 md:px-4">
              <label
                class="mb-6 mt-8 block font-bold"
                for="entry_interested_course"
              >
                現時点で最も興味のあるコースをひとつ選択ください
                <YaTeCommonBadgeRequired class="ml-2" />
              </label>
              <div
                v-for="interested_course_category in interestedCourseCategories"
                :key="interested_course_category.id"
                class="mb-4 grid grid-cols-1 gap-2 border border-co-gray-300 bg-white px-4 py-3 md:grid-cols-2 md:gap-x-8"
              >
                <!-- 副業・フリーランス・転職 -->
                <div
                  v-for="interested_course in interested_course_category.interested_courses"
                  :key="interested_course.id"
                  class="form-control"
                >
                  <label class="cursor-pointer !items-baseline">
                    <input
                      :id="'interested_course_' + interested_course.id"
                      v-model="interestedCourseSelected"
                      type="radio"
                      :value="String(interested_course.id)"
                      name="interested-course"
                      required
                      class="radio"
                    >
                    <span class="ml-2">{{ interested_course.title }}</span>
                  </label>
                </div>
              </div>
              <span
                v-if="errors.interested_course_ids && errors.interested_course_ids.length !== 0"
                class="te-invalid-feedback inline-block"
              >
                {{ errors.interested_course_ids.join(", ") }}
              </span>
            </div>

            <!-- 注釈 -->
            <p class="ya-te-note mb-5 text-co-gray-500">
              ※個人情報の取扱い
              <br>
              下記フォームにご記入いただく回答内容は、LINEヤフー株式会社および株式会社ブリューアスがそれぞれ取得します。
              LINEヤフー株式会社は、本フォームに入力いただいたお客さまの情報を<a
                href="https://www.lycorp.co.jp/ja/company/privacypolicy/"
                target="_blank"
              >
                プライバシーポリシー
              </a>に定めるところにより、その目的の遂行に必要な範囲において取り扱うこととします。
              株式会社ブリューアスは、本フォームに入力いただいたお客さまのデータを<a
                href="/yahoo/privacypolicy"
                target="_blank"
              >プライバシーポリシー
              </a>に定めるところにより、その目的の遂行に必要な範囲において取り扱うこととします。
            </p>
            <p class="ya-te-note mb-5 text-co-gray-500">
              ※免責
              <br>
              氏名・カナが不正と判断される場合、もしくはメールアドレスが不着・不正と判断される場合、ご予約をキャンセルとさせていただきます。あらかじめご了承ください。
            </p>
            <div class="co-display-14">
              このサイトはreCAPTCHAによって保護されており、Googleの<a
                href="https://policies.google.com/privacy"
                target="_blank"
              >プライバシーポリシー</a>と<a
                href="https://policies.google.com/terms"
                target="_blank"
              >利用規約</a>が適用されます。
            </div>

            <!-- エラーメッセージ -->
            <span
              v-if="Object.keys(errors).length !== 0"
              class="te-invalid-feedback inline-block py-2 text-center"
            >
              入力いただいた情報に誤りまたは不足があるようです。
            </span>
            <span
              v-if="applicationErrors.length !== 0"
              class="te-invalid-feedback inline-block py-2"
              v-html="applicationErrors[0]"
            />
          </div>

          <!-- モーダルフッター -->
          <div class="modal-action absolute inset-x-0 bottom-0 mt-0 flex w-full items-center justify-between border-t border-co-gray-200 p-4 backdrop-blur md:justify-around">
            <button
              type="button"
              class="co-btn-tertiary"
              @click="closeModal"
            >
              キャンセル
            </button>
            <button
              v-show="isSubmitButton"
              class="ya-te-btn-primary ya-te-bg-primary !w-auto !p-3 !text-[1rem]"
              @click="onSubmit"
            >
              無料相談を予約する
            </button>
            <!-- ローディング中アラート -->
            <div v-show="isSubmitLoading">
              <div class="flex flex-col items-center justify-center md:flex-row">
                <FontAwesomeIcon
                  :icon="faSpinner"
                  class="fa-fw fa-spin mr-1 text-[1.5rem]"
                />
                <p class="co-display-13 mt-2 text-center md:ml-3 md:mt-0">
                  日程を確定させております。
                  <br class="md:hidden">
                  そのままお待ちください。
                </p>
              </div>
            </div>
          </div>
        </form>
        <!-- モーダルの外側をクリックしたら閉じる -->
        <button
          class="modal-backdrop"
          @click="closeModal"
        >
          close
        </button>
      </dialog>
    </Teleport>
  </client-only>
</template>

<script lang="ts">
import { postTrialMiniCourseSchedule } from '~/utility/fetchAPI'
import validatePostTrialMiniCourseSchedule from '~/utility/validation/validatePostTrialMiniCourseScheduleForLyta'
import { daysOfWeek } from '~/utility/utility'
import YaTeCommonBadgeOptional from '~/components/YaTe/YaTeCommon/Badge/YaTeCommonBadgeOptional.vue'
import YaTeCommonBadgeRequired from '~/components/YaTe/YaTeCommon/Badge/YaTeCommonBadgeRequired.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faClockNine } from '@awesome.me/kit-65d95819ce/icons/classic/regular'
import { faSpinner } from '@awesome.me/kit-65d95819ce/icons/classic/solid'

interface Data {
  email: string
  lastName: string
  lastNameKana: string
  firstName: string
  firstNameKana: string
  tel: string
  interestedCourseSelected: string
  learningPurposeSelected: string | null
  entryMotivationLevelSelected: string | null
  mentoringCheckboxSelected: string[]
  asking: string
  errors: any
  applicationErrors: string[]
  isSubmitButton: boolean // 次へ進むボタン
  isSubmitLoading: boolean // ローディング中アラート
}

interface Methods {
  onSubmit: () => void
  dateToDateStr: (date: Date) => string
}

interface Computed {}

interface Props {
  appointmentForm: any
  appointmentDate: Date
  courses: string
  purpose: string
  courseScreenName: string
  pageUrl: string
}

const options = {
  async setup() {
    // モーダルの開閉制御
    const calendarModal = ref<HTMLDialogElement | null>(null)

    const showModal = () => {
      if (calendarModal.value) {
        calendarModal.value.showModal()
      }
    }

    const closeModal = () => {
      if (calendarModal.value) {
        calendarModal.value.close()
      }
    }

    return {
      calendarModal,
      showModal,
      closeModal,
      faClockNine,
      faSpinner,
    }
  },
  props: {
    appointmentForm: {
      default: null,
    },
    appointmentDate: {
      default: null,
    },
    courses: {
      default: null,
    },
    purpose: {
      default: null,
    },
    courseScreenName: {
      default: null,
    },
    pageUrl: {
      default: '',
    },
  },
  data() {
    return {
      lastName: '',
      firstName: '',
      lastNameKana: '',
      firstNameKana: '',
      email: '',
      tel: '',
      interestedCourseSelected: '',
      learningPurposeSelected: null,
      entryMotivationLevelSelected: null,
      mentoringCheckboxSelected: [],
      asking: '',
      errors: {},
      applicationErrors: [],
      isSubmitButton: true, // 次へ進むボタン
      isSubmitLoading: false, // ローディング中アラート
    }
  },
  async mounted() {
    try {
      await this.$recaptcha.init()
    } catch (e) {
      // 何もしない
    }
  },
  computed: {
    interestedCourseCategories() {
      if (!this.appointmentForm) return [{ id: '0', title: '読み込み中' }]
      return this.appointmentForm.interested_course_collection
    },
    learningPurposeOptions() {
      if (!this.appointmentForm) return [{ value: '0', text: '読み込み中' }]
      const options = Object.entries<number>(
        this.appointmentForm.learning_purpose_collection_for_trial
      )
        .filter((option) => {
          switch (option[0]) {
            case 'アルバイト・パートの仕事にしたい':
              return false
            case 'まずは少し稼げるようになりたい':
              return false
            default:
              return true
          }
        })
        .sort(function (a, b) {
          return a[1] < b[1] ? -1 : 1
        })
        .map((ment: any) => {
          return { value: `${ment[1]}`, text: ment[0] }
        }) as any
      options.unshift({ text: 'お選びください', value: null })
      return options
    },
    entryMotivationLevelOptions() {
      if (!this.appointmentForm) return [{ value: '0', text: '読み込み中' }]
      const options = Object.entries(
        this.appointmentForm.entry_motivation_level_collection_for_trial
      ).map((ment: any) => {
        return { value: `${ment[1]}`, text: ment[0] }
      }) as any
      options.unshift({ text: 'お選びください', value: null })
      return options
    },
    checkboxOptions() {
      if (!this.appointmentForm) return [{ value: '0', text: '読み込み中' }]
      return this.appointmentForm.mentoring_style_collection.map(
        (ment: [number, string][]) => {
          return { value: `${ment[0]}`, text: ment[1] }
        }
      )
    },
  },
  beforeDestroy() {
    this.$recaptcha.destroy()
  },
  methods: {
    async onSubmit() {
      this.errors = {}
      this.applicationErrors = []
      const token: string = await this.$recaptcha.execute('trialEntry')

      const body: any = {
        trial_appointment_account_form: {
          appointment_form: {
            begins_at: `${this.appointmentDate}`,
            tel: this.tel,
            interested_course_ids: [this.interestedCourseSelected],
            learning_purpose: this.learningPurposeSelected,
            entry_motivation_level: this.entryMotivationLevelSelected,
            mentoring_style: this.mentoringCheckboxSelected,
            asking: this.asking,
            courses: this.courses,
            purpose: this.purpose,
            range: 2,
          },
          trial_entry: {
            last_name: this.lastName,
            first_name: this.firstName,
            last_name_kana: this.lastNameKana,
            first_name_kana: this.firstNameKana,
            email: this.email,
            gclid: this.$route.query.gclid || '',
          },
          page_url: this.pageUrl,
        },
        recaptcha_token: token,
      }
      validatePostTrialMiniCourseSchedule(body)
        .then(() => {
          postTrialMiniCourseSchedule(this.$api, this.courseScreenName, body)
            .then((response) => {
              if (!this.pageUrl) {
                if (window.top !== null) {
                  window.top.location.href = `/ly/counseling/completed`
                } else {
                  window.location.href = `/ly/counseling/completed`
                }
              } else if (window.top !== null) {
                window.top.location.href = `/ly/counseling/completed?token=${response.token}&id=${response.id}`
              } else {
                window.location.href = `/ly/counseling/completed?token=${response.token}&id=${response.id}`
              }
            })
            .catch((errors: any) => {
              this.applicationErrors = errors?.response?.data?.errors
              // 次へ進むボタンを表示してローディングを非表示に
              this.isSubmitButton = true
              this.isSubmitLoading = false
            })
        })
        .catch((errors: any) => {
          this.errors = errors
          // 次へ進むボタンを表示してローディングを非表示に
          this.isSubmitButton = true
          this.isSubmitLoading = false
        })
      // 次へ進むボタンを非表示にしてローディングを表示
      this.isSubmitButton = false
      this.isSubmitLoading = true
    },
    dateToDateStr(date) {
      if (!date) return '-/- (-)'
      return `${date.getMonth() + 1}/${date.getDate()} (${daysOfWeek(
        date
      )}) ${String(date.getHours()).padStart(1, '0')}:${String(
        date.getMinutes()
      ).padStart(2, '0')}`
    },
  },
  components: {
    YaTeCommonBadgeOptional,
    YaTeCommonBadgeRequired,
    FontAwesomeIcon,
  },
}

export default defineNuxtComponent(options)
</script>

<style scoped>
@import url("~/assets/css/YaTe/YaTeCommon/YaTeCommon.css");

.te-invalid-feedback {
  width: 100%;
  margin-top: .25rem;
  font-size: .875em;
  color: #dc3545;
}
</style>
